import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Box,
  Button,
  Chip,
  Divider,
  Grid,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from "@mui/material";

const API_BASE = "/api/ebay-intelligence";

const numberText = (value) => {
  const n = Number(value || 0);
  return Number.isFinite(n) ? n.toLocaleString() : String(value || 0);
};

const dateText = (value) => {
  if (!value) return "-";
  const parsed = new Date(String(value).replace(" ", "T"));
  return Number.isNaN(parsed.getTime()) ? String(value) : parsed.toLocaleString();
};

const priorityColor = (priority) => {
  if (priority === "high") return "error";
  if (priority === "medium") return "warning";
  if (priority === "low") return "info";
  return "default";
};

const statusColor = (status) => {
  if (status === "attention_required") return "error";
  if (status === "review_recommended" || status === "data_incomplete") return "warning";
  if (status === "steady") return "success";
  return "default";
};

const outcomeColor = (status) => {
  if (status === "accepted" || status === "actioned") return "success";
  if (status === "rejected" || status === "ignored") return "default";
  if (status === "deferred" || status === "needs_follow_up") return "warning";
  return "info";
};

const labelText = (value) => String(value || "").replace(/_/g, " ");

function MetricCard({ label, value, helper }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Typography variant="caption" color="text.secondary">{label}</Typography>
      <Typography variant="h5" sx={{ fontWeight: 800, mt: 0.5 }}>{value ?? "--"}</Typography>
      {helper && <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>{helper}</Typography>}
    </Paper>
  );
}

function DecisionCard({ card, outcome, onDecision, decisionSavingKey, onOpenActionQueue, onOpenImports }) {
  const metrics = card.metrics || {};
  const chips = [
    metrics.open_high_priority_actions !== undefined ? `High priority ${numberText(metrics.open_high_priority_actions)}` : null,
    metrics.open_actions !== undefined ? `Open ${numberText(metrics.open_actions)}` : null,
    metrics.stale_sync_count !== undefined ? `Stale sync ${numberText(metrics.stale_sync_count)}` : null,
    metrics.failed_sync_count !== undefined ? `Failed sync ${numberText(metrics.failed_sync_count)}` : null,
    metrics.ad_spend_risk !== undefined ? `Ad risk ${numberText(metrics.ad_spend_risk)}` : null,
    metrics.repricing_candidates !== undefined ? `Repricing ${numberText(metrics.repricing_candidates)}` : null,
    metrics.candidate_blocker_count !== undefined ? `Blockers ${numberText(metrics.candidate_blocker_count)}` : null,
  ].filter(Boolean);
  const staleOps = metrics.stale_operations || [];
  const failedOps = metrics.failed_operations || [];
  const isActionQueue = card.target_module === "ebayintelligence" && ["review_open_action_queue", "review_sold_out_cleanup"].includes(card.key);
  const isImports = ["refresh_ebay_telemetry", "refresh_import_pipeline"].includes(card.key);
  const outcomeStatus = outcome?.outcome_status || card.decision_status || "open";
  const decisionDisabled = Boolean(decisionSavingKey);

  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 3, height: "100%" }}>
      <Stack spacing={1.25} sx={{ height: "100%" }}>
        <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
          <Chip size="small" color={priorityColor(card.priority)} label={labelText(card.priority || "priority")} />
          <Chip size="small" variant="outlined" label={`Score ${numberText(card.score)}`} />
          <Chip size="small" variant="outlined" label={labelText(card.target_module)} />
          <Chip size="small" color={outcomeColor(outcomeStatus)} label={`Decision ${labelText(outcomeStatus)}`} />
        </Stack>
        <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>{card.title}</Typography>
        <Typography variant="body2" color="text.secondary">{card.reason}</Typography>
        <Typography variant="body2">{card.recommended_manual_action}</Typography>
        {card.expected_impact && (
          <Typography variant="caption" color="text.secondary">
            <strong>Expected impact:</strong> {card.expected_impact}
          </Typography>
        )}
        {card.risk && (
          <Typography variant="caption" color="text.secondary">
            <strong>Risk:</strong> {card.risk}
          </Typography>
        )}
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          {chips.map((item) => <Chip key={item} size="small" label={item} sx={{ mb: 1 }} />)}
          {staleOps.slice(0, 3).map((item) => <Chip key={`stale-${item}`} size="small" color="warning" label={`${labelText(item)} stale`} sx={{ mb: 1 }} />)}
          {failedOps.slice(0, 3).map((item) => <Chip key={`failed-${item}`} size="small" color="error" label={`${labelText(item)} failed`} sx={{ mb: 1 }} />)}
        </Stack>
        <Box sx={{ flex: 1 }} />
        <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap" }}>
          <Button size="small" variant="contained" onClick={() => onDecision(card, "accepted")} disabled={decisionDisabled}>Accept</Button>
          <Button size="small" variant="outlined" onClick={() => onDecision(card, "rejected")} disabled={decisionDisabled}>Reject</Button>
          <Button size="small" variant="outlined" onClick={() => onDecision(card, "deferred")} disabled={decisionDisabled}>Defer</Button>
          {isActionQueue && <Button size="small" variant="text" onClick={onOpenActionQueue}>Open Queue</Button>}
          {isImports && <Button size="small" variant="text" onClick={onOpenImports}>Open Imports</Button>}
        </Stack>
      </Stack>
    </Paper>
  );
}

export default function DailyDecisionAgentPanel({ onOpenActionQueue, onOpenImports }) {
  const [agent, setAgent] = useState(null);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [decisionSavingKey, setDecisionSavingKey] = useState(null);
  const [outcomes, setOutcomes] = useState({});
  const [message, setMessage] = useState(null);
  const [error, setError] = useState(null);

  const loadAgent = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const [agentResponse, outcomePayload] = await Promise.all([
        fetch(`${API_BASE}/daily-decision-agent?limit=1000`),
        fetch(`${API_BASE}/recommendation-outcomes?recommendation_source=daily_decision_agent&include_resolved=1&limit=100`)
          .then(async (response) => ({ response, json: await response.json() }))
          .catch(() => null),
      ]);
      const json = await agentResponse.json();
      if (!agentResponse.ok || json.ok === false) throw new Error(json.error || json.message || "Failed to load daily decision agent");
      setAgent(json);
      if (outcomePayload?.response?.ok && outcomePayload.json?.schema_ready !== false) {
        const nextOutcomes = {};
        (outcomePayload.json.rows || []).forEach((row) => {
          if (row.source_recommendation_id) nextOutcomes[row.source_recommendation_id] = row;
        });
        setOutcomes(nextOutcomes);
      }
    } catch (e) {
      setError(e.message || String(e));
      setAgent(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadAgent();
  }, [loadAgent]);

  const recordRun = async () => {
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/daily-decision-agent/runs`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ limit: 1000, source: "dashboard" }),
      });
      const json = await res.json();
      if (!res.ok || json.ok === false) throw new Error(json.error || json.message || "Failed to record daily decision run");
      setAgent((current) => ({ ...(current || {}), ...json, latest_run: json.latest_run || json.created_run }));
      setMessage("Daily decision run recorded locally.");
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setSaving(false);
    }
  };

  const saveDecisionOutcome = async (card, status) => {
    const sourceId = card.key;
    setDecisionSavingKey(sourceId);
    setError(null);
    setMessage(null);
    try {
      const res = await fetch(`${API_BASE}/recommendation-outcomes`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          recommendation_source: "daily_decision_agent",
          source_recommendation_id: sourceId,
          recommendation_key: card.key,
          recommendation_title: card.title,
          recommendation_summary: `${card.reason || ""} ${card.recommended_manual_action || ""}`.trim(),
          outcome_status: status,
          outcome_reason: status,
          business_feedback: `Daily Agent ${labelText(status)} from dashboard. Expected impact: ${card.expected_impact || "not captured"}. Risk: ${card.risk || "not captured"}.`,
        }),
      });
      const json = await res.json();
      if (!res.ok || json.ok === false) throw new Error(json.error || json.message || "Failed to save daily decision outcome");
      setOutcomes((current) => ({ ...current, [sourceId]: json.outcome }));
      setMessage(`Daily decision marked ${labelText(status)} locally.`);
    } catch (e) {
      setError(e.message || String(e));
    } finally {
      setDecisionSavingKey(null);
    }
  };

  const summary = agent?.summary || {};
  const cards = Array.isArray(agent?.decision_cards) ? agent.decision_cards : [];
  const history = Array.isArray(agent?.run_history) ? agent.run_history : [];
  const warnings = Array.isArray(agent?.warnings) ? agent.warnings : [];
  const safety = Array.isArray(agent?.safety_boundaries) ? agent.safety_boundaries : [];
  const headlineStatus = summary.headline_status || agent?.signals?.daily_summary?.headline_status;
  const visibleWarnings = useMemo(() => warnings.filter(Boolean).slice(0, 4), [warnings]);

  return (
    <Stack spacing={2}>
      {(loading || saving || decisionSavingKey) && <LinearProgress />}
      {error && <Alert severity="error">{error}</Alert>}
      {message && <Alert severity="success">{message}</Alert>}
      {agent?.schema_ready === false && <Alert severity="warning">Manual SQL install is required before daily decision runs can be recorded.</Alert>}

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} justifyContent="space-between" alignItems={{ xs: "stretch", md: "flex-start" }}>
          <Box>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap", mb: 0.5 }}>
              <Typography variant="h6" sx={{ fontWeight: 800 }}>Daily Decision Agent</Typography>
              <Chip size="small" color={statusColor(headlineStatus)} label={labelText(headlineStatus || "pending")} />
              <Chip size="small" variant="outlined" label={agent?.agent_version || "v1"} />
            </Stack>
            <Typography variant="body2" color="text.secondary">
              {summary.headline || "Local advisory cards from eBay Intelligence, store signals, advertising telemetry, and repricing readiness."}
            </Typography>
          </Box>
          <Stack direction={{ xs: "column", sm: "row" }} spacing={1}>
            <Button variant="outlined" onClick={loadAgent} disabled={loading || saving || Boolean(decisionSavingKey)}>Refresh</Button>
            <Button variant="contained" onClick={recordRun} disabled={loading || saving || Boolean(decisionSavingKey) || agent?.schema_ready === false}>Record Run</Button>
          </Stack>
        </Stack>
      </Paper>

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Decision cards" value={numberText(summary.decision_count)} helper="Current advisory snapshot" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="High priority" value={numberText(summary.high_priority_count)} helper="Cards marked high" /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Open actions" value={numberText(summary.open_actions)} helper={`${numberText(summary.open_high_priority_actions)} high-priority actions`} /></Grid>
        <Grid item xs={12} sm={6} md={3}><MetricCard label="Warnings" value={numberText(summary.data_warning_count)} helper="Freshness or schema signals" /></Grid>
      </Grid>

      {visibleWarnings.length > 0 && (
        <Alert severity="warning">
          {visibleWarnings.join(" ")}
        </Alert>
      )}

      <Grid container spacing={2}>
        {cards.map((card) => (
          <Grid item xs={12} md={6} key={card.key}>
            <DecisionCard
              card={card}
              outcome={outcomes[card.key]}
              onDecision={saveDecisionOutcome}
              decisionSavingKey={decisionSavingKey}
              onOpenActionQueue={onOpenActionQueue}
              onOpenImports={onOpenImports}
            />
          </Grid>
        ))}
        {!loading && cards.length === 0 && (
          <Grid item xs={12}>
            <Alert severity="info">No daily decision cards are available yet.</Alert>
          </Grid>
        )}
      </Grid>

      <Paper variant="outlined" sx={{ p: 2, borderRadius: 3 }}>
        <Stack direction={{ xs: "column", md: "row" }} spacing={2} divider={<Divider orientation="vertical" flexItem />}>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Latest local run</Typography>
            {agent?.latest_run ? (
              <Stack spacing={0.5}>
                <Typography variant="body2">Run {agent.latest_run.id} / {agent.latest_run.run_key}</Typography>
                <Typography variant="body2" color="text.secondary">Created {dateText(agent.latest_run.created_at)} from {agent.latest_run.source || "manual"}</Typography>
                <Stack direction="row" spacing={1} sx={{ flexWrap: "wrap", pt: 0.5 }}>
                  <Chip size="small" label={`${numberText(agent.latest_run.decision_count)} decisions`} />
                  <Chip size="small" color={agent.latest_run.high_priority_count ? "error" : "default"} label={`${numberText(agent.latest_run.high_priority_count)} high`} />
                  <Chip size="small" label={`${numberText(agent.latest_run.data_warning_count)} warnings`} />
                </Stack>
              </Stack>
            ) : (
              <Typography variant="body2" color="text.secondary">No local daily decision run is recorded yet.</Typography>
            )}
          </Box>
          <Box sx={{ flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 1 }}>Run history</Typography>
            <Stack spacing={0.75}>
              {history.slice(0, 4).map((run) => (
                <Stack key={run.id || run.run_key} direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
                  <Chip size="small" label={`#${run.id}`} />
                  <Typography variant="body2">{dateText(run.created_at)}</Typography>
                  <Typography variant="body2" color="text.secondary">{numberText(run.decision_count)} decision(s)</Typography>
                </Stack>
              ))}
              {!history.length && <Typography variant="body2" color="text.secondary">History appears after the first local run is recorded.</Typography>}
            </Stack>
          </Box>
        </Stack>
        {safety.length > 0 && (
          <Alert severity="info" sx={{ mt: 2 }}>
            {safety.join(" ")}
          </Alert>
        )}
      </Paper>
    </Stack>
  );
}
